import { ItemMaster } from '../../../services/apiTypes';
import {
  INSPECTION_EXCEL_FILE_NAME,
  CATEGORY_SHEET_NAME,
  TYPE_SHEET_NAME,
  NG_SAMPLE_SHEET_NAME,
  ITEM_SHEET_NAME,
  ATTRIBUTE_SHEET_NAME,
} from '../../../_constants/manageConst';
import {
  isGetCategoryMaster,
  isGetItemMaster,
  isGetTypeMaster,
} from '../../../_utils/typeGuardUtils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import { API_CODE } from '../../../_constants/apiConst';
import { getTypeMaster } from '../../../services/api/typeMaster';
import { ItemForExcel } from '../../../types/ExcelType';
import XLSX from 'xlsx';
import { createExportExcelName } from '../../../_utils/utils';
import { I18nMessage } from '../../../types/type';
import { getCategoryMaster } from '../../../services/api/categoryMaster';
import { getItemMaster } from '../../../services/api/itemMaster';

type useExportProps = {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  showApiError: (
    apiError: unknown,
    apiCode: string,
    onCloseCallback?: () => void,
  ) => void;
  showTypeError: (apiCode: string) => void;
  showClientError: (
    message: string | I18nMessage,
    onCloseCallback?: () => void,
  ) => void;
};

const useExport = ({
  setIsLoading,
  showApiError,
  showTypeError,
  showClientError,
}: useExportProps) => {
  const { userData } = useSelector((state: RootState) => state.userInfo);

  /** 診断項目マスタのエクスポート */
  const doExportInspectionItemMaster = async () => {
    setIsLoading(true);
    try {
      const workbook = XLSX.utils.book_new();

      await addCategorySheet(workbook);
      await addTypeSheet(workbook);
      await addItemSheet(workbook);

      XLSX.writeFile(
        workbook,
        createExportExcelName(INSPECTION_EXCEL_FILE_NAME, userData.dateFormat),
        { compression: true },
      );
    } catch (error) {
      if (error instanceof Error && error.message !== 'ERR_GET_MASTER') {
        showClientError('ERR_EXPORT_INSPECTION_ITEM');
      }
      return;
    } finally {
      setIsLoading(false);
    }
  };

  /** 大項目シート追加 */
  const addCategorySheet = async (workbook: XLSX.WorkBook): Promise<void> => {
    try {
      const response = await getCategoryMaster();
      if (!isGetCategoryMaster(response)) {
        showTypeError(API_CODE.CATEGORY_MASTER);
      }
      const categories = response.map((c) => {
        const { displayName, ...category } = c;
        return category;
      });
      const worksheet = XLSX.utils.json_to_sheet(categories);
      XLSX.utils.book_append_sheet(workbook, worksheet, CATEGORY_SHEET_NAME);
    } catch (error) {
      showApiError(error, API_CODE.CATEGORY_MASTER);
      throw new Error('ERR_GET_MASTER');
    }
  };

  /** 中項目シート追加 */
  const addTypeSheet = async (workbook: XLSX.WorkBook): Promise<void> => {
    try {
      const response = await getTypeMaster();
      if (!isGetTypeMaster(response)) {
        showTypeError(API_CODE.TYPE_MASTER);
      }
      const typeData = response.map(
        ({ typeImageMaster, displayName, ...type }) => type,
      );
      const typeImageData = response.flatMap((type) =>
        type.typeImageMaster.map((image) => ({
          typeId: type.typeId,
          ...image,
        })),
      );
      const worksheetType = XLSX.utils.json_to_sheet(typeData);
      XLSX.utils.book_append_sheet(workbook, worksheetType, TYPE_SHEET_NAME);

      const worksheetImage = XLSX.utils.json_to_sheet(typeImageData);
      XLSX.utils.book_append_sheet(
        workbook,
        worksheetImage,
        NG_SAMPLE_SHEET_NAME,
      );
    } catch (error) {
      showApiError(error, API_CODE.TYPE_MASTER);
      throw new Error('ERR_GET_MASTER');
    }
  };

  /** 小項目シート追加 */
  const addItemSheet = async (workbook: XLSX.WorkBook): Promise<void> => {
    try {
      const response = await getItemMaster();
      if (!isGetItemMaster(response)) {
        showTypeError(API_CODE.ITEM_MASTER);
      }
      const formattedData = convertItemMasterToExcel(response.itemList);
      const itemSheet = XLSX.utils.json_to_sheet(formattedData);
      const attributeList = [
        ...response.attribute1MasterList,
        ...response.attribute2MasterList,
        ...response.attribute3MasterList,
        ...response.attribute5MasterList,
        ...response.attribute6MasterList,
        ...response.brandMasterList,
      ];
      attributeList.sort((a, b) => a.id - b.id);
      const attributeSheet = XLSX.utils.json_to_sheet(attributeList ?? []);
      XLSX.utils.book_append_sheet(workbook, itemSheet, ITEM_SHEET_NAME);
      XLSX.utils.book_append_sheet(
        workbook,
        attributeSheet,
        ATTRIBUTE_SHEET_NAME,
      );
    } catch (error) {
      showApiError(error, API_CODE.ITEM_MASTER);
      throw new Error('ERR_GET_MASTER');
    }
  };

  /** APIで取得した小項目データをExcelに出力するフォーマットに変換 */
  const convertItemMasterToExcel = (itemList: ItemMaster[]) => {
    const formattedList: ItemForExcel[] = [];
    itemList.forEach((item) => {
      const {
        inputType,
        criteria,
        selectedAttribute1,
        selectedAttribute2,
        selectedAttribute3,
        selectedAttribute5,
        selectedAttribute6,
        selectedBrandName,
        selectedConditions,
        pCode,
        fCode,
        rCodeList,
        // displayNameは出力対象外
        displayName,
        ...rest
      } = item;
      // 入力方法は数値入力かどうかに変換
      const isNumberInput = inputType === 1 || '';
      // 基準値のオブジェクトを各カラムに変換
      const criteriaText = criteria.text;
      const criteriaMin = criteria.min;
      const criteriaMax = criteria.max;
      // 属性・ブランドは名前を繋げて表示
      const attribute1 = selectedAttribute1
        ? selectedAttribute1.map((attr) => attr.name).join(',')
        : '';
      const attribute2 = selectedAttribute2
        ? selectedAttribute2.map((attr) => attr.name).join(',')
        : '';
      const attribute3 = selectedAttribute3
        ? selectedAttribute3.map((attr) => attr.name).join(',')
        : '';
      const attribute5 = selectedAttribute5
        ? selectedAttribute5.map((attr) => attr.name).join(',')
        : '';
      const attribute6 = selectedAttribute6
        ? selectedAttribute6.map((attr) => attr.name).join(',')
        : '';
      const brandName = selectedBrandName
        ? selectedBrandName.map((attr) => attr.name).join(',')
        : '';

      // Rコードのリストはつなげて表示
      const rCode = rCodeList.join(',');

      const formattedItem: ItemForExcel = {
        ...rest,
        isNumberInput,
        criteriaText,
        criteriaMin,
        criteriaMax,
        attribute1,
        attribute2,
        attribute3,
        attribute5,
        attribute6,
        brandName,
        // カラム名変更
        conditions: selectedConditions,
        pCode,
        fCode,
        rCode,
      };

      formattedList.push(formattedItem);
    });
    return formattedList;
  };

  return { doExportInspectionItemMaster };
};

export default useExport;
